import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../api/context";
import type { ProspectTarget } from "../../api/prospector-types";

export type ProspectorState = "loading" | "on" | "off";

export interface ProspectorProbe {
  readonly status: ProspectorState;
  /** Targets the probe loaded (empty unless `status === "on"`). Informational: the Targets page still loads its own list. */
  readonly targets: readonly ProspectTarget[];
}

const NONE: readonly ProspectTarget[] = [];

/**
 * Prospector is behind a server-side flag: the API answers 404 on every route while it is off. OWN-376:
 * `GET /prospecting/enabled` (never 404) is asked first and `/targets` only when it says on, so no page
 * logs a 404. Any failure reads as `off` so the nav entry and routes stay hidden; it never throws.
 * `enabled = false` skips the calls.
 */
export function useProspectorProbe(workspaceId: string, enabled = true): ProspectorProbe {
  const api = useApi();
  const flag = useQuery({
    queryKey: ["prospector-enabled", workspaceId],
    enabled,
    queryFn: async (): Promise<boolean> => {
      try {
        return await api.getProspectorEnabled(workspaceId);
      } catch {
        return false;
      }
    },
  });
  const flagOn = flag.data === true;
  const query = useQuery({
    queryKey: ["prospector-probe", workspaceId],
    enabled: enabled && flagOn,
    queryFn: async (): Promise<readonly ProspectTarget[] | null> => {
      try {
        return await api.listProspectTargets(workspaceId);
      } catch {
        return null;
      }
    },
  });
  if (!enabled) return { status: "off", targets: NONE };
  if (flag.isPending) return { status: "loading", targets: NONE };
  if (!flagOn) return { status: "off", targets: NONE };
  if (query.isPending) return { status: "loading", targets: NONE };
  return query.data ? { status: "on", targets: query.data } : { status: "off", targets: NONE };
}

export function useProspectorEnabled(workspaceId: string, enabled = true): ProspectorState {
  return useProspectorProbe(workspaceId, enabled).status;
}
